<template>
  <AnalyticsCard
    title="Visitors"
    :subtitle="`New against returning, per ${bucketWord}`"
    :loading="loading"
    :empty="isEmpty"
    empty-title="No visitors in this period"
    empty-hint="Once people start finding your booking page, this shows whether they are new faces or people coming back."
    :height="288"
  >
    <template #actions>
      <span v-for="s in LEGEND" :key="s.label"
        class="hidden md:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-3">
        <span class="w-2.5 h-2.5 rounded-full inline-block" :style="{ backgroundColor: s.color }"></span>{{ s.label }}
      </span>
    </template>
    <div ref="el" class="w-full h-72"></div>
  </AnalyticsCard>
</template>

<script setup>
import { computed, toRef } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { useAnalyticsChart, baseOptions, PALETTE } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  data:    { type: Array, default: () => [] },
  bucket:  { type: String, default: 'day' },
  loading: { type: Boolean, default: false },
})

const LEGEND = [
  { label: 'New',       color: PALETTE.green },
  { label: 'Returning', color: PALETTE.violet },
]

const bucketWord = computed(() => ({ hour: 'hour', day: 'day', week: 'week', month: 'month' }[props.bucket] || 'day'))
const isEmpty = computed(() => !props.loading && props.data.length > 0 && props.data.every(d => !d.total))

// Stacked rather than overlaid: new and returning are two halves of one total,
// and drawing them as separate lines makes the reader add them up by eye to
// answer "how many people came".
const { el } = useAnalyticsChart((_node, tokens) => {
  if (!props.data.length) return null
  const base = baseOptions(tokens)
  const labels = props.data.map(d => d.label)

  return {
    ...base,
    chart: { ...base.chart, type: 'column', height: 288, spacing: [16, 16, 8, 4] },
    xAxis: {
      ...base.xAxis,
      categories: labels,
      tickInterval: Math.max(1, Math.ceil(labels.length / 7)),
      crosshair: { color: tokens.grid },
    },
    yAxis: { ...base.yAxis, min: 0, allowDecimals: false },
    tooltip: { ...base.tooltip, shared: true },
    plotOptions: {
      column: { stacking: 'normal', borderWidth: 0, borderRadius: 3, pointPadding: 0.04, groupPadding: 0.1 },
    },
    series: [
      { name: 'Returning', data: props.data.map(d => d.returning), color: PALETTE.violet },
      { name: 'New',       data: props.data.map(d => d.new),       color: PALETTE.green },
    ],
  }
}, [toRef(props, 'data')])
</script>
